// © Microsoft Corporation. All rights reserved.

import { CoreLibAppErrors } from "@msnews/diagnostics";
import { Telemetry } from "@msnews/telemetry-contracts";

export interface Subscription {
    /** Function to unsubscribe */
    unsubscribe: () => void;
}

interface SubscriptionInternal extends Subscription {
    /** Position of the callback in the subscription array, used internally */
    index: number;
}

/**
 * Extension of Map with option to execute callbacks when a specific entry is added/updated
 * @class -  SubscribableMap
 */
export class SubscribableMap<Key, Value> extends Map<Key, Value> {
    /** Subscription map */
    private subscriptions: Map<Key, ((data?: any) => void)[]> = new Map();

    constructor(private identifier: string) {
        super();
    }

    /**
     * Set function that adds subscription info
     * @param key - the key
     * @param value - the value
     */
    public set(key: Key, value: Value): this {
        super.set(key, value);

        const subscriptionCallbacks = this.subscriptions.get(key) || [];

        if (!subscriptionCallbacks.length) {
            return this;
        }

        subscriptionCallbacks.forEach((callback: (data: Value) => void, index: number) => {
            try {
                // Unsubscribe sets the callback to null, check before executing
                if (callback) {
                    callback(value);
                }
            } catch (error) {
                Telemetry.sendAppErrorEvent({
                    ...CoreLibAppErrors.SubscriptionCallbackExecError,
                    message: "Failed to execute subscription callback",
                    pb: {
                        ...CoreLibAppErrors.SubscriptionCallbackExecError.pb,
                        customMessage: `Error executing subscription callback for key [${key}] at handler index [${index}]`,
                        instanceId: this.identifier,
                        errorMessage: error && error.message
                    }
                });
            }
        });

        return this;
    }

    /**
     * Registers a function that executes when the map entry is already present, newly added or update.
     * @param key - the cache key
     * @param callback - the callback to execute when the cache entry is added/updated
     */
    public subscribe<T = any>(key: Key, callback: (data: T) => void): Subscription {
        const subscription = this.subscribeInternal(key, callback);

        // If the value is already present, invoke the callback immediately
        if (this.has(key)) {
            this.tryExecuteSubscriptionCallback(key, subscription.index, this.get(key));
        }

        return {
            unsubscribe: subscription.unsubscribe
        };
    }

    /**
     * Internal subscribe implementation
     * @param key  - the cache key
     * @param callback - the callback to execute when the cache entry is added/updated
     * @returns object containing unsubscribe callback and index of the callback in the subscription array
     */
    private subscribeInternal(key: Key, callback: (data: any) => void): SubscriptionInternal {
        const subscriptions = this.subscriptions.get(key) || [];

        this.subscriptions.set(key, [...subscriptions, callback]);
        const index = subscriptions.length;

        return {
            unsubscribe: () => {
                const callbacks = this.subscriptions.get(key) || [];

                if (callbacks[index]) {
                    callbacks[index] = null;
                }
                this.subscriptions.set(key, callbacks);
            },
            index
        };
    }

    /**
     * Retrieves and executes the subscription callback if it exists
     * @param key subscription key
     * @param index index of the callback function in the subscriptions array
     * @param data optional data for the callback
     */
    private tryExecuteSubscriptionCallback(key: Key, index: number, data?: Value): void {
        // Always read from subscriptions Map to make sure unsubscribed subscription callbacks are not executed
        const subscriptions = this.subscriptions.get(key);

        if (!subscriptions || !subscriptions.length) {
            return;
        }

        const callback = subscriptions[index];

        if (typeof callback === "function") {
            callback(data);
        }
    }
}